// Use the `/pure` entry point so Stripe.js is NOT auto-injected at import
// time. The default `@stripe/stripe-js` entry adds a <script> tag for
// js.stripe.com on every page load (even the landing page where checkout is
// never used), which Lighthouse flags as render-blocking and adds ~4s to
// FCP/LCP. With `/pure`, the script is only fetched the first time
// `loadStripe()` is actually called (i.e. when the user opens checkout).
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";

const clientToken = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;
const environment: "sandbox" | "live" =
  clientToken?.startsWith("pk_test_") ? "sandbox" : "live";

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Returns a Stripe.js promise. If the publishable token is missing from the
 * environment (e.g. a production build that never had `.env.production`
 * generated), this resolves to `null` instead of throwing. Throwing during
 * render previously unmounted the entire React tree and left the user staring
 * at a blank page on the published site.
 *
 * Callers must handle the `null` case — see `isPaymentsConfigured()` below.
 */
export function getStripe(): Promise<Stripe | null> {
  if (!clientToken) {
    return Promise.resolve(null);
  }
  if (!stripePromise) {
    stripePromise = loadStripe(clientToken);
  }
  return stripePromise;
}

export function getStripeEnvironment() {
  return environment;
}

export function isPaymentsTestMode() {
  return environment === "sandbox";
}

/**
 * True if a publishable Stripe token is present in the current build. Used by
 * UI to render a friendly fallback instead of mounting the embedded checkout
 * (which would otherwise hang or crash with no key).
 */
export function isPaymentsConfigured() {
  return Boolean(clientToken);
}
